import { PaymentMethodType } from "./payment-gateway-types.ts";

export const planTypes = ["deck_producer", "plus", "pro", "teacher"] as const;
export type PlanType = (typeof planTypes)[number];
export const paidPlanTypes = ["pro", "teacher"] as const;
export type PaidPlanType = (typeof paidPlanTypes)[number];
export type PlanDuration = 1 | 6 | 12;

export const planDurations: PlanDuration[] = [1, 6, 12];

export type PaymentPlan = {
  type: PaidPlanType;
  title: string;
  settings: {
    includesTeacherStatistics: boolean;
    paymentMethods: PaymentMethodType[];
  };
  price: Record<PaymentMethodType, Partial<Record<PlanDuration, number>>>;
  sort: number;
};

export const plans = {
  pro: {
    type: "pro",
    title: "MemoCard Pro",
    settings: {
      includesTeacherStatistics: false,
      paymentMethods: [PaymentMethodType.Usd, PaymentMethodType.Stars],
    },
    price: {
      [PaymentMethodType.Usd]: {
        1: 4,
        6: 18,
        12: 24,
      },
      [PaymentMethodType.Stars]: {
        1: 250,
        6: 1125,
        12: 1500,
      },
    },
    sort: 2,
  },
  teacher: {
    type: "teacher",
    title: "MemoCard Teacher",
    settings: {
      includesTeacherStatistics: true,
      paymentMethods: [PaymentMethodType.Usd, PaymentMethodType.Stars],
    },
    price: {
      [PaymentMethodType.Usd]: {
        1: 10,
        6: 55,
        12: 100,
      },
      [PaymentMethodType.Stars]: {
        1: 770,
        6: 4235,
        12: 7700,
      },
    },
    sort: 3,
  },
} satisfies Record<PaidPlanType, PaymentPlan>;

export const paymentPlans = Object.values(plans).sort(
  (a, b) => a.sort - b.sort,
);

export const getPaymentPlan = (planType: PaidPlanType): PaymentPlan => {
  switch (planType) {
    case "pro":
      return plans.pro;
    case "teacher":
      return plans.teacher;
    default:
      return planType satisfies never;
  }
};

export const isPlanDurationAvailable = (
  method: PaymentMethodType,
  plan: PaymentPlan,
  duration: PlanDuration,
) => {
  return plan.price[method][duration] !== undefined;
};

export const getPlanPrice = (
  method: PaymentMethodType,
  plan: PaymentPlan,
  duration: PlanDuration,
) => {
  const price = plan.price[method][duration];
  if (price === undefined) {
    throw new Error(
      `Price is not configured: ${plan.type} ${method} ${duration}`,
    );
  }
  return price;
};

export const getPlanDiscountForDuration = (
  method: PaymentMethodType,
  plan: PaymentPlan,
  duration: PlanDuration,
) => {
  if (duration === 1) {
    return 0;
  }

  const monthlyPrice = plan.price[method][1];
  const durationPrice = plan.price[method][duration];
  if (monthlyPrice === undefined || durationPrice === undefined) {
    return 0;
  }

  return +(1 - durationPrice / (monthlyPrice * duration)).toFixed(3);
};

export const isPaidPlanType = (
  planType: PlanType,
): planType is PaidPlanType => {
  return planType === "pro" || planType === "teacher";
};
